// Formulario.js — A definição ÚNICA de campo de formulário do site.
//
// Por que existir: havia QUATRO cópias do mesmo estilo de <input> espalhadas
// pelo projeto (perfil, autenticação, checkout e os formulários de avaliação),
// e a do checkout tinha medidas próprias — `px-3.5 py-2.5` contra `px-4 py-3`
// das outras três. Na prática os campos onde a pessoa digita o endereço de
// entrega eram visivelmente menores que os campos do perfil, sem que ninguém
// tivesse decidido isso.
//
// Aqui mora `px-4 py-3` (o valor de 3 das 4 cópias, portanto o que já estava
// aprovado na maior parte das telas) e o anel de foco.
//
// POR QUE O FOCO É AZUL E NÃO LARANJA: o laranja é a cor de AÇÃO do site — o
// botão que envia o formulário é laranja. Um campo com foco laranja logo acima
// de um botão laranja faz os dois lerem como a mesma coisa, e o foco deixa de
// dizer "é aqui que você está digitando". `insigne-navy-light` é da família
// navy, tem token nos dois temas (#6C6CDF no escuro, #5B5BD6 no claro) e não
// disputa com o botão. Antes disto era `#1d8efd`, um azul literal que não
// existia na paleta e não acompanhava o tema claro.
//
// Nota sobre a borda: `border` é uma classe que o Bootstrap declara com
// `!important` (ver o bloco "CONFLITO COM BOOTSTRAP" no App.css), então o
// `focus:border-*` abaixo não pinta nada nos campos que também usam `border`.
// Quem comunica o foco de verdade é o `ring`. A classe fica porque é gratuita
// e volta a valer no dia em que o Bootstrap sair.
import React from "react";

export const campoCls =
  "w-full rounded-lg bg-insigne-navy-deep border border-line px-4 py-3 text-sm text-white " +
  "placeholder:text-white/40 outline-none transition-colors " +
  "focus:border-insigne-navy-light focus:ring-1 focus:ring-insigne-navy-light";

const rotuloCls = "text-sm font-medium text-white";

/**
 * O "+N" do campo do perfil que paga Insigne Coins.
 *
 * DOIS ESTADOS, desde 11/09/2026 (pedido do dono): AMARELO enquanto o dado
 * ainda rende, e RISCADO EM CINZA depois que o servidor pagou. Antes o selo
 * sumia ao pagar — e sumir é um recibo que ninguém lê: a pessoa voltava ao
 * perfil e não sabia se tinha ganhado alguma coisa ou se o campo nunca valeu.
 * Riscado, a tela mostra ao mesmo tempo o que ela já recebeu e o que falta,
 * que é o que empurra a preencher o resto.
 *
 * MORA AQUI, e não no perfil, porque a mesma marca precisa aparecer em lugares
 * que NÃO são um `Campo` — o envio da foto e o cabeçalho do endereço de
 * entrega. Duas cópias divergiriam de tamanho e de cor na primeira alteração,
 * e a marca só funciona se for sempre a mesma coisa na tela inteira.
 *
 * O `title` NÃO é enfeite: "+10" sozinho promete uma recompensa recorrente, e
 * ela é paga uma vez por dado, para sempre. Sem a frase, quem apagar e
 * repreencher o campo esperando ganhar de novo conclui que o site ficou com os
 * coins dele.
 *
 * Quem decide o número e o estado é o servidor (ver o hook useCoinsPerfil). O
 * número riscado é o que a pessoa DE FATO recebeu, não o preço de hoje: quem
 * ganhou 10 pelo telefone antes de ele passar a valer 15 vê "+10" riscado.
 * 0 ou nada não desenha.
 *
 * `title` só substitui a frase do estado PENDENTE; o recibo tem sempre a mesma.
 * `minimo` entra na frase quando o dado só vale a partir de um tamanho (a bio).
 *
 * O risco mora num `<span>` próprio, e não no envelope: o envelope é
 * `inline-flex`, e decoração de texto não atravessa com segurança para os
 * itens de um flex — o "+10" podia sair sem o traço justamente no navegador de
 * quem mais precisava ver.
 */
export const BonusCoins = ({ coins, recebido = false, minimo, title, className = "" }) => {
  const n = Math.max(0, Math.floor(Number(coins) || 0));
  if (!n) return null;
  const texto = recebido
    ? `Você já recebeu ${n} Insigne Coins por este dado.`
    : title ||
      (minimo
        ? `Com pelo menos ${minimo} caracteres, este dado rende ${n} Insigne Coins, uma vez só.`
        : `Preencher este dado rende ${n} Insigne Coins, uma vez só.`);
  return (
    <span
      title={texto}
      aria-label={texto}
      className={
        "shrink-0 inline-flex items-center gap-1 text-xs font-bold cursor-help " +
        (recebido ? "text-white/35 " : "text-insigne-bonus ") +
        className
      }
    >
      <span className={recebido ? "line-through decoration-2" : ""}>+{n}</span>
      <i className="bi bi-coin" aria-hidden="true" />
    </span>
  );
};

// Rótulo + controle + dica opcional.
// `dica` é sinônimo de `hint`: as duas cópias que este componente substituiu
// usavam nomes diferentes para a mesma coisa, e aceitar os dois evita uma
// renomeação em massa que não muda nada na tela.
//
// `premio` (o objeto de `useCoinsPerfil().premioDoCampo`) e `bonus` (só o
// número, o formato antigo) são OPCIONAIS e existem só para o perfil. Este
// arquivo também desenha o login e o checkout, telas que não têm coins
// nenhum: por isso o envelope flex do rótulo só nasce quando há prêmio, e
// sem as props o <label> continua sendo exatamente o mesmo elemento solto de
// antes. Um campo que não passa nenhuma das duas não pode mudar um pixel.
export const Campo = ({ label, hint, dica, bonus, premio, children, className = "" }) => {
  const coins = premio ? premio.coins : bonus;
  return (
  <div className={"flex flex-col gap-2 " + className}>
    {label &&
      (coins > 0 ? (
        <div className="flex items-center justify-between gap-3">
          <label className={rotuloCls}>{label}</label>
          <BonusCoins
            coins={coins}
            recebido={!!premio?.recebido}
            minimo={premio?.minimo}
            title={premio?.titulo}
          />
        </div>
      ) : (
        <label className={rotuloCls}>{label}</label>
      ))}
    {children}
    {(hint || dica) && (
      <p className="text-xs text-white/50 leading-relaxed">{hint || dica}</p>
    )}
  </div>
  );
};

export const TextInput = ({ className = "", ...props }) => (
  <input className={campoCls + " " + className} {...props} />
);

export const TextArea = ({ className = "", rows = 4, ...props }) => (
  <textarea rows={rows} className={campoCls + " resize-y " + className} {...props} />
);
